import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { extractErrorMessage } from '../utils/errorHandler';
import './TemplateList.css';
import './OfferList.css';
import { SavedOfferSummary } from '../types';
import { formatDateTime } from '../utils/dates';

interface OfferFilters {
  title: string;
  client: string;
  updatedFrom: string; // yyyy-mm-dd
  updatedTo: string;   // yyyy-mm-dd
}

const EMPTY_FILTERS: OfferFilters = {
  title: '',
  client: '',
  updatedFrom: '',
  updatedTo: '',
};

// Case- and diacritic-insensitive, so "lodz" finds "Łódź" and "zolc" finds "Żółć".
const normalize = (value: string): string =>
  value
    .toLocaleLowerCase('pl')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining accents left by NFD
    .replace(/ł/g, 'l');        // l-stroke has no NFD decomposition

const OfferList: React.FC = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState<SavedOfferSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<OfferFilters>(EMPTY_FILTERS);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/offers');
      setOffers(response.data);
    } catch (err: any) {
      console.error('Error fetching offers:', err);
      setError(`Nie udało się wczytać listy ofert: ${extractErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOffer = async (id: number, title: string) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć ofertę "${title}"?`)) {
      return;
    }

    try {
      await axios.delete(`/api/offers/${id}`);
      setOffers(offers.filter(o => o.id !== id));
    } catch (err: any) {
      console.error('Error deleting offer:', err);
      setError(`Nie udało się usunąć oferty: ${extractErrorMessage(err)}`);
    }
  };

  const updateFilter = (key: keyof OfferFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some(v => v !== ''),
    [filters]
  );

  const filteredOffers = useMemo(() => {
    const title = normalize(filters.title.trim());
    const client = normalize(filters.client.trim());

    // updated_at arrives without a timezone, so the browser reads it as local time --
    // the same frame formatDateTime renders in. Build the bounds locally to match.
    const from = filters.updatedFrom ? new Date(`${filters.updatedFrom}T00:00:00`) : null;
    const to = filters.updatedTo ? new Date(`${filters.updatedTo}T23:59:59.999`) : null;

    return offers.filter(offer => {
      if (title && !normalize(offer.title || '').includes(title)) {
        return false;
      }
      if (client && !normalize(offer.client_name || '').includes(client)) {
        return false;
      }
      if (from || to) {
        const updated = new Date(offer.updated_at);
        if (Number.isNaN(updated.getTime())) return false;
        if (from && updated < from) return false;
        if (to && updated > to) return false;
      }
      return true;
    });
  }, [offers, filters]);

  if (loading) {
    return <div className="loading">Ładowanie ofert...</div>;
  }

  return (
    <div className="container">
      <div className="card template-list">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h1>Moje Oferty</h1>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/offer-generator')}
          >
            Nowa Oferta
          </button>
        </div>

        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}

          {offers.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Nie masz jeszcze żadnych zapisanych ofert.</p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/offer-generator')}
              >
                Utwórz pierwszą ofertę
              </button>
            </div>
          ) : (
            <>
              <div className="offer-filters">
                <div className="offer-filter-group">
                  <label htmlFor="offer-filter-title">Nazwa</label>
                  <input
                    id="offer-filter-title"
                    type="text"
                    className="offer-filter-input"
                    placeholder="Szukaj po nazwie..."
                    value={filters.title}
                    onChange={e => updateFilter('title', e.target.value)}
                  />
                </div>

                <div className="offer-filter-group">
                  <label htmlFor="offer-filter-client">Klient</label>
                  <input
                    id="offer-filter-client"
                    type="text"
                    className="offer-filter-input"
                    placeholder="Szukaj po kliencie..."
                    value={filters.client}
                    onChange={e => updateFilter('client', e.target.value)}
                  />
                </div>

                <div className="offer-filter-group">
                  <label htmlFor="offer-filter-from">Ostatnia aktualizacja</label>
                  <div className="offer-filter-dates">
                    <input
                      id="offer-filter-from"
                      type="date"
                      className="offer-filter-input offer-filter-date"
                      value={filters.updatedFrom}
                      max={filters.updatedTo || undefined}
                      onChange={e => updateFilter('updatedFrom', e.target.value)}
                      aria-label="Zaktualizowana od"
                    />
                    <span className="offer-filter-dash">–</span>
                    <input
                      type="date"
                      className="offer-filter-input offer-filter-date"
                      value={filters.updatedTo}
                      min={filters.updatedFrom || undefined}
                      onChange={e => updateFilter('updatedTo', e.target.value)}
                      aria-label="Zaktualizowana do"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-outline btn-sm offer-filter-clear"
                  onClick={() => setFilters(EMPTY_FILTERS)}
                  disabled={!hasActiveFilters}
                >
                  Wyczyść filtry
                </button>
              </div>

              <div className="offer-filter-count">
                {hasActiveFilters
                  ? `Znaleziono ${filteredOffers.length} z ${offers.length} ofert`
                  : `Wszystkie oferty: ${offers.length}`}
              </div>

              {filteredOffers.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">Żadna oferta nie pasuje do wybranych filtrów.</p>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => setFilters(EMPTY_FILTERS)}
                  >
                    Wyczyść filtry
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Nazwa</th>
                        <th>Klient</th>
                        <th>Produkty</th>
                        <th>Ostatnia aktualizacja</th>
                        <th>Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOffers.map(offer => (
                        <tr key={offer.id}>
                          <td>{offer.title}</td>
                          <td>{offer.client_name || '-'}</td>
                          <td>{offer.product_count}</td>
                          <td>{formatDateTime(offer.updated_at)}</td>
                          <td>
                            <div className="btn-group">
                              <Link
                                to={`/offer-generator/${offer.id}`}
                                className="btn btn-outline-primary"
                              >
                                Otwórz
                              </Link>

                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDeleteOffer(offer.id, offer.title)}
                              >
                                Usuń
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfferList;
